import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import publicAxios from '../../api/publicAxios';

// Optimized selectors with memoization
export const selectDoctorsState = (state) => state.doctors;

export const selectDoctors = createSelector(
  [selectDoctorsState],
  (doctorsState) => doctorsState.data || []
);

export const selectDoctorsCount = createSelector(
  [selectDoctorsState],
  (doctorsState) => doctorsState.count || 0
);

export const selectDoctorsLoading = createSelector(
  [selectDoctorsState],
  (doctorsState) => doctorsState.loading || false
);

export const selectDoctorsError = createSelector(
  [selectDoctorsState],
  (doctorsState) => doctorsState.error || null
);

export const selectDoctorsFilters = createSelector(
  [selectDoctorsState],
  (doctorsState) => doctorsState.filters || {}
);

// Enhanced fetchDoctors thunk with retry logic and better error handling
export const fetchDoctors = createAsyncThunk(
  'doctors/fetchDoctors',
  async (params = {}, { rejectWithValue, dispatch }) => {
    const maxRetries = parseInt(process.env.REACT_APP_RETRY_ATTEMPTS) || 3;
    const retryDelay = parseInt(process.env.REACT_APP_RETRY_DELAY) || 2000;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`Fetching doctors - attempt ${attempt}/${maxRetries}`, params);
        
        // Ensure params is an object
        const safeParams = params || {};
        
        const queryParams = new URLSearchParams();
        
        // Add search params - only include non-empty values with safe checks
        // Support both 'query' and 'search' parameter names
        if (safeParams.query && typeof safeParams.query === 'string' && safeParams.query.trim()) {
          queryParams.append('search', safeParams.query.trim());
        }
        if (safeParams.search && typeof safeParams.search === 'string' && safeParams.search.trim()) {
          queryParams.append('search', safeParams.search.trim());
        }
        // Support both 'location' and 'city' parameter names
        if (safeParams.location && typeof safeParams.location === 'string' && safeParams.location.trim()) {
          queryParams.append('city', safeParams.location.trim());
        }
        if (safeParams.city && typeof safeParams.city === 'string' && safeParams.city.trim()) {
          queryParams.append('city', safeParams.city.trim());
        }
        // Support 'speciality' parameter name
        if (safeParams.speciality && typeof safeParams.speciality === 'string' && safeParams.speciality.trim()) {
          queryParams.append('speciality', safeParams.speciality.trim());
        }
        if (safeParams.language && typeof safeParams.language === 'string' && safeParams.language.trim()) {
          queryParams.append('language', safeParams.language.trim());
        }
        // Support both camelCase and snake_case parameter names
        const minRating = safeParams.minRating || safeParams.min_rating || safeParams.rating;
        if (minRating && minRating > 0) queryParams.append('min_rating', minRating);
        
        if (safeParams.distance && safeParams.distance > 0) queryParams.append('max_distance', safeParams.distance);
        
        if (safeParams.page && safeParams.page > 0) queryParams.append('page', safeParams.page);
        
        const minFee = safeParams.minFee || safeParams.min_fee;
        if (minFee !== undefined && minFee !== '' && minFee >= 0) queryParams.append('min_fee', minFee);
        
        const maxFee = safeParams.maxFee || safeParams.max_fee;
        if (maxFee && maxFee !== '' && maxFee > 0) queryParams.append('max_fee', maxFee);
        
        if (safeParams.min_experience && safeParams.min_experience > 0) queryParams.append('min_experience', safeParams.min_experience);
        if (safeParams.max_experience && safeParams.max_experience > 0) queryParams.append('max_experience', safeParams.max_experience);
        
        // Availability filtering
        if (safeParams.availability && typeof safeParams.availability === 'string' && safeParams.availability !== 'all') {
          queryParams.append('availability', safeParams.availability);
        }
        
        // Consultation type filtering - map frontend values to backend values
        if (safeParams.consultationType && typeof safeParams.consultationType === 'string' && safeParams.consultationType !== 'all') {
          const consultationTypeMap = {
            'video': 'video',
            'phone': 'phone',
            'in-person': 'in_person',
          };
          const backendType = consultationTypeMap[safeParams.consultationType] || safeParams.consultationType;
          queryParams.append('consultation_type', backendType);
        }
        
        // Also support old parameter name for backward compatibility
        if (safeParams.consultation_type && typeof safeParams.consultation_type === 'string' && safeParams.consultation_type !== 'all') {
          queryParams.append('consultation_type', safeParams.consultation_type);
        }
        
        // Gender filtering
        if (safeParams.gender && typeof safeParams.gender === 'string' && safeParams.gender !== 'all') {
          queryParams.append('gender', safeParams.gender);
        }
        
        // Always include page_size for consistent pagination
        if (!queryParams.has('page_size')) {
          queryParams.append('page_size', '10');
        }
        
        // Add sorting - default to newest if not specified
        const sortBy = safeParams.sortBy || 'newest';
        if (sortBy) {
          let sortField = '';
          switch (sortBy) {
            case 'rating':
              sortField = '-experience'; // Fallback since average_rating is not a direct field
              break;
            case 'experience':
              sortField = '-experience';
              break;
            case 'fee':
              sortField = 'consultation_fee';
              break;
            case 'name':
              sortField = 'name';
              break;
            case 'newest':
              sortField = '-created_at';
              break;
            default:
              sortField = '-created_at'; // Default to newest first
          }
          queryParams.append('ordering', sortField);
        }

        // Build the URL - handle empty query params
        const queryString = queryParams.toString();
        const url = queryString ? `/doctors/list/?${queryString}` : '/doctors/list/';
        
        const response = await publicAxios.get(url);
        
        // Validate response data
        if (!response.data || typeof response.data !== 'object') {
          throw new Error('Invalid response format from server');
        }
        
        // Handle both paginated and non-paginated responses
        const results = Array.isArray(response.data.results) 
          ? response.data.results 
          : (Array.isArray(response.data) ? response.data : []);
        const count = response.data.count || results.length || 0;
        
        console.log(`Successfully fetched ${results.length} doctors (total: ${count})`);
        return {
          results,
          count,
          next: response.data.next || null,
          previous: response.data.previous || null,
        };
        
      } catch (error) {
        console.error(`Error fetching doctors (attempt ${attempt}/${maxRetries}):`, error);
        
        // If this is the last attempt, reject with error
        if (attempt === maxRetries) {
          let errorMessage = 'Failed to fetch doctors';
          
          if (error.code === 'ECONNABORTED') {
            errorMessage = 'Request timeout - please check your connection';
          } else if (error.response?.status === 0) {
            errorMessage = 'Network error - unable to connect to server';
          } else if (error.response?.status >= 500) {
            errorMessage = 'Server error - please try again later';
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          return rejectWithValue(errorMessage);
        }
        
        // Wait before retrying
        console.log(`Retrying in ${retryDelay}ms...`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
  }
);

const doctorsSlice = createSlice({
  name: 'doctors',
  initialState: {
    data: [],
    count: 0,
    next: null,
    previous: null,
    loading: false,
    error: null,
    lastFetch: null,
    filters: {
      query: '',
      location: '',
      speciality: '',
      language: '',
      rating: null,
      distance: 10,
      sortBy: 'newest',  // Changed from 'distance' to 'newest'
      page: 1,
    },
  },
  reducers: {
    updateFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload, page: 1 };
    },
    setPage: (state, action) => {
      state.filters.page = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {
        query: '',
        location: '',
        speciality: '',
        language: '',
        rating: null,
        distance: 10,
        sortBy: 'newest',  // Changed from 'distance' to 'newest'
        page: 1,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDoctors.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDoctors.fulfilled, (state, action) => {
        state.loading = false;
        // Ensure we always have valid data
        state.data = Array.isArray(action.payload.results) ? action.payload.results : [];
        state.count = action.payload.count || 0;
        state.next = action.payload.next || null;
        state.previous = action.payload.previous || null;
        state.lastFetch = Date.now();
      })
      .addCase(fetchDoctors.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { updateFilters, setPage, clearFilters } = doctorsSlice.actions;
export default doctorsSlice.reducer; 